import {
  CSV_MAX_ROWS,
  CSV_TEMPLATE_HEADERS,
  HEADER_TO_FIELD_KEY,
  SYSTEM_FIELDS,
  REQUIRED_FIELD_KEYS,
  DEFAULT_ROW_VALUES,
  MAX_WARMUP_INCREMENT,
  CONNECT_INBOX_CSV_ERROR_CODE
} from "./constants";

const splitCsvLine = line => {
  const fields = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === `"`) {
      if (inQuotes && line[i + 1] === `"`) {
        current += `"`;
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      fields.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  fields.push(current.trim());
  return fields;
};

export const parseCsvText = (text = "") => {
  const lines = text
    .split(/\r\n|\n|\r/)
    .filter(line => line.trim().length > 0);
  if (!lines.length) {
    return { headers: [], rows: [] };
  }
  const headers = splitCsvLine(lines[0]);
  const rows = lines.slice(1).map(line => splitCsvLine(line));
  return { headers, rows };
};

const normalizeHeaderKey = value =>
  (value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const NORMALIZED_HEADER_LOOKUP = (() => {
  const lookup = {};
  Object.entries(HEADER_TO_FIELD_KEY).forEach(([label, key]) => {
    lookup[normalizeHeaderKey(label)] = key;
  });
  SYSTEM_FIELDS.forEach(field => {
    lookup[normalizeHeaderKey(field.key)] = field.key;
  });
  return lookup;
})();

export const buildAutoMapping = (headers = []) =>
  headers.map(header => {
    const key = NORMALIZED_HEADER_LOOKUP[normalizeHeaderKey(header)];
    return {
      rawHeader: header,
      fieldKey: key || "ignore",
      autoMapped: Boolean(key)
    };
  });

export const isMappingMissingRequiredFields = mapping => {
  const mappedKeys = mapping.map(col => col.fieldKey);
  return REQUIRED_FIELD_KEYS.some(required => !mappedKeys.includes(required));
};

export const mapRowsToObjects = (rawRows = [], mapping = []) =>
  rawRows.map((rawRow, rowIndex) => {
    const rowObj = { rowIndex };
    mapping.forEach((col, colIndex) => {
      if (col.fieldKey && col.fieldKey !== "ignore") {
        rowObj[col.fieldKey] = (rawRow[colIndex] || "").trim();
      }
    });
    return rowObj;
  });

const detectProvider = smtpHost => {
  const host = (smtpHost || "").toLowerCase();
  if (host.includes("gmail")) return "GOOGLE";
  if (host.includes("office365") || host.includes("outlook")) return "MICROSOFT";
  if (host.includes("zoho")) return "ZOHO";
  if (host.includes("yahoo")) return "YAHOO";
  if (host.includes("icloud") || host.includes("mail.me.com")) return "ICLOUD";
  if (host.includes("secureserver.net")) return "GODADDY";
  if (host.includes("protonmail") || host === "127.0.0.1") return "PROTONMAIL";
  if (host.includes("fastmail")) return "FASTMAIL";
  return "UNKNOWN";
};

export const validateRow = row => {
  const provider = detectProvider(row.smtpHost);
  const missing = REQUIRED_FIELD_KEYS.filter(key => !row[key]);
  if (missing.length) {
    const fieldLabel =
      SYSTEM_FIELDS.find(field => field.key === missing[0])?.label ||
      missing[0];
    return {
      ...row,
      provider,
      status: "error",
      reason: `Missing ${fieldLabel}`,
      code: CONNECT_INBOX_CSV_ERROR_CODE.MISSING_FIELD
    };
  }
  if (provider === "GOOGLE" && row.smtpPassword && row.smtpPassword.length !== 16) {
    return {
      ...row,
      provider,
      status: "error",
      reason: "Invalid app password",
      code: CONNECT_INBOX_CSV_ERROR_CODE.INVALID_APP_PASSWORD
    };
  }
  return { ...row, provider, status: "ready", reason: "", code: "" };
};

export const applyRowDefaults = row => ({
  ...row,
  dailyLimit: row.dailyLimit || DEFAULT_ROW_VALUES.dailyLimit,
  warmupEnabled:
    row.warmupEnabled === undefined || row.warmupEnabled === ""
      ? DEFAULT_ROW_VALUES.warmupEnabled
      : String(row.warmupEnabled).toLowerCase() === "true",
  warmupLimit: row.warmupLimit || DEFAULT_ROW_VALUES.warmupLimit,
  warmupIncrement: Math.min(
    Number(row.warmupIncrement) || DEFAULT_ROW_VALUES.warmupIncrement,
    MAX_WARMUP_INCREMENT
  )
});

export const validateCsvFile = file => {
  if (!file) {
    return "Please choose a file to upload.";
  }
  if (!file.name.toLowerCase().endsWith(".csv")) {
    return "Only .csv files are supported.";
  }
  return "";
};

export const validateRowCount = rowCount => {
  if (rowCount === 0) {
    return "No rows found in this file.";
  }
  if (rowCount > CSV_MAX_ROWS) {
    return `You can upload only a maximum of ${CSV_MAX_ROWS} inboxes in a CSV.`;
  }
  return "";
};

export const downloadSampleTemplate = () => {
  const sampleRow = [
    "emily.paris@gmail.com",
    "Emily",
    "Paris",
    "emily.paris@gmail.com",
    "abcdefghijklmnop",
    "smtp.gmail.com",
    "465",
    "emily.paris@gmail.com",
    "abcdefghijklmnop",
    "imap.gmail.com",
    "993",
    "30",
    "TRUE",
    "20",
    "2"
  ];
  const csvContent = [CSV_TEMPLATE_HEADERS.join(","), sampleRow.join(",")].join(
    "\n"
  );
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "Salesgear - Bulk Inbox Connect Sample.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const buildBackendRowPayload = row => ({
  rowIndex: row.rowIndex,
  email: row.email,
  firstName: row.firstName || "",
  lastName: row.lastName || "",
  smtpUsername: row.smtpUsername || row.email,
  smtpPassword: row.smtpPassword,
  smtpHost: row.smtpHost,
  smtpPort: row.smtpPort,
  imapUsername: row.imapUsername || row.email,
  imapPassword: row.imapPassword,
  imapHost: row.imapHost,
  imapPort: row.imapPort,
  dailyLimit: row.dailyLimit,
  warmupEnabled: row.warmupEnabled,
  warmupLimit: row.warmupLimit,
  warmupIncrement: row.warmupIncrement
});
